import * as THREE from 'three'
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader'
import {
  ARCH_DEFAULT_COLOR,
  BRACES_DEVIATION_ARCH_COLOR,
  DEVIATION_COLORS,
  UNKNOWN_TOOTH_COLOR,
  BASE_DEFAULT_OPACITY,
  UPPER_BASE_NAME,
  LOWER_BASE_NAME,
  OVERLAY_DEFAULT_OPACITY
} from './archesViewerConsts'
import { TreatmentTypes } from 'consts/treatmentConsts'
import { getObjectUrl } from 'utils/mediaUtils'
import { Poses } from 'components/common/Grin3DViewer/Grin3DViewerConsts'
import { TeethTrackingStatuses } from '../txTrackerConsts'
import { getAlignersToothTrackingStatus, getToothTrackingStatus } from '../TeethTracking/teethTrackingStatusUtils'

const STATUS_TO_COLOR = {
  [TeethTrackingStatuses.Tracking]: DEVIATION_COLORS.MINOR_OFFSET,
  [TeethTrackingStatuses.PartiallyTracking]: DEVIATION_COLORS.SMALL_OFFSET,
  [TeethTrackingStatuses.NotTracking]: DEVIATION_COLORS.SIGNIFICANT_OFFSET
}

export const getDeviationColor = toothData => {
  if (!toothData) return null
  const status = getAlignersToothTrackingStatus(toothData)
  return STATUS_TO_COLOR[status] ?? null
}

// The viewer parses meshes with OBJLoader. From model V1.0.1 the tracker writes an STL to
// currentActualStl and the OBJ to currentActualObj; older results only have currentActualStl,
// which held the OBJ.
export const getActualArchMeshFile = archS3Keys => archS3Keys?.currentActualObj ?? archS3Keys?.currentActualStl

export const getRetentionToothColor = toothData => {
  const status = getToothTrackingStatus({ toothData, treatmentType: TreatmentTypes.Aligners })
  if (status === TeethTrackingStatuses.Unknown) return UNKNOWN_TOOTH_COLOR
  if (
    status === TeethTrackingStatuses.TrackingNoMovement ||
    status === TeethTrackingStatuses.TrackingNoMovementPlanned
  ) {
    return DEVIATION_COLORS.MINOR_OFFSET
  }
  return STATUS_TO_COLOR[status] ?? ARCH_DEFAULT_COLOR
}

export const loadArchModels = ({
  scene,
  pose,
  archesData,
  teethData,
  treatmentType,
  isRetentionResult = false,
  upperArchInitRotation,
  lowerArchInitRotation,
  upperArchOffset = 12,
  lowerArchOffset
}) => {
  const objLoader = new OBJLoader()
  const baseArchMaterial = new THREE.MeshStandardMaterial({
    color: ARCH_DEFAULT_COLOR,
    opacity: BASE_DEFAULT_OPACITY,
    transparent: true
  })

  archesData.forEach(arch => {
    objLoader.load(getObjectUrl(arch.file), object => {
      const isOtherOcclusalArch = (pose === Poses.Upper && !arch.isUpper) || (pose === Poses.Lower && arch.isUpper)
      if (isOtherOcclusalArch) {
        return
      }

      if (!arch.isUpper && lowerArchInitRotation) {
        object.rotation.set(lowerArchInitRotation.x, lowerArchInitRotation.y, lowerArchInitRotation.z)
      }

      if (arch.isUpper && upperArchInitRotation) {
        object.rotation.set(upperArchInitRotation.x, upperArchInitRotation.y, upperArchInitRotation.z)
      }

      if (arch.isUpper) {
        // For upper arch add Y axis offset from lower arch
        object.position.set(0, upperArchOffset, 0)
      } else {
        if (lowerArchOffset) {
          // For upper arch add an offset
          object.position.set(0, lowerArchOffset, 0)
        }
      }

      object.traverse(toothMesh => {
        if (toothMesh instanceof THREE.Mesh) {
          if (!arch.isBase) {
            // Logic for current arch
            const toothData = teethData?.[`${toothMesh.name}`]
            const toothColor = isRetentionResult
              ? getRetentionToothColor(toothData)
              : toothData?.unknown && !toothData?.no_movement_planned
              ? UNKNOWN_TOOTH_COLOR
              : treatmentType === TreatmentTypes.Aligners
              ? getDeviationColor(toothData)
              : BRACES_DEVIATION_ARCH_COLOR

            const currentToothMaterial = new THREE.MeshStandardMaterial({
              color: new THREE.Color(toothColor ?? ARCH_DEFAULT_COLOR),
              opacity: OVERLAY_DEFAULT_OPACITY
            })

            toothMesh.material = currentToothMaterial
          } else {
            toothMesh.material = baseArchMaterial
          }

          // Mirrors the mesh becuase the coordinates convention is different from the
          // original STL viewer which the STL was created
          toothMesh.scale.set(-1, 1, 1)
          // Enable display of unscanned side
          toothMesh.material.side = THREE.DoubleSide
          // Enable picking by setting the object to be an intersection object
          toothMesh.userData.isSelectable = true
        }
      })

      if (arch.isBase) {
        object.name = arch.isUpper ? UPPER_BASE_NAME : LOWER_BASE_NAME
      }
      scene.add(object)
    })
  })
}

export const handleToothClick = intersectsObjects => {
  const toothMesh = intersectsObjects[0].object
  if (toothMesh.userData.isSelectable) {
    toothMesh.parent.traverse(child => {
      if (child.isMesh) {
        // Logic for reset changes for previous click
      }
    })

    // Clone material to separate from parent
    toothMesh.material = toothMesh.material.clone()
    // Logic for clicked toothMesh
  }
}

export const setObjectOpacity = ({ object, opacity }) =>
  object.children.forEach(mesh => (mesh.material.opacity = opacity))
